import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { MoreHorizontal, Pencil, Archive, ArchiveRestore, Trash2 } from "lucide-react";
import { toast } from "sonner";

const FIELDS = [["name", "Name"], ["legal_name", "Legal name"], ["address", "Address"], ["state_code", "State code (e.g. WB)"]];

function EditDialog({ org, open, onOpenChange, onDone }) {
  const [form, setForm] = useState({});
  const edit = useMutation({
    mutationFn: async () => (await api.put(`/admin/organisations/${org.id}`, form)).data,
    onSuccess: () => { toast.success("Organisation updated"); onOpenChange(false); onDone(); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  useEffect(() => {
    if (open) { setForm({ name: org.name || "", legal_name: org.legal_name || "", address: org.address || "", state_code: org.state_code || "" }); }
  }, [open, org]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Edit organisation</DialogTitle><DialogDescription>Update the organisation details.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          {FIELDS.map(([k, label]) => (
            <div key={k}><Label>{label}</Label>
              <Input value={form[k] ?? ""} maxLength={k === "state_code" ? 2 : undefined}
                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                className={`mt-1.5 ${k === "state_code" ? "uppercase" : ""}`} data-testid={`edit-org-${k}`} />
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button disabled={!form.name?.trim() || edit.isPending} onClick={() => edit.mutate()} data-testid="edit-org-submit">
            {edit.isPending ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteDialog({ org, open, onOpenChange, onDeleted }) {
  const [confirm, setConfirm] = useState("");
  const del = useMutation({
    mutationFn: async () => (await api.delete(`/admin/organisations/${org.id}`)).data,
    onSuccess: () => { toast.success("Organisation deleted"); onOpenChange(false); onDeleted(); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  return (
    <Dialog open={open} onOpenChange={(v) => { setConfirm(""); onOpenChange(v); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete organisation</DialogTitle>
          <DialogDescription>
            This permanently deletes <strong>{org.name}</strong> with its masters, templates, invites and admin access.
            It is only allowed when the organisation has no employees. Type the name to confirm.
          </DialogDescription>
        </DialogHeader>
        <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={org.name} data-testid="delete-org-confirm-input" />
        <DialogFooter>
          <Button variant="destructive" disabled={confirm !== org.name || del.isPending} onClick={() => del.mutate()} data-testid="delete-org-submit">
            {del.isPending ? "Deleting…" : "Delete permanently"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export const OrgActions = ({ org, onChanged, onDeleted }) => {
  const qc = useQueryClient();
  const [dialog, setDialog] = useState(null);
  const refresh = () => { qc.invalidateQueries({ queryKey: ["orgs"] }); qc.invalidateQueries({ queryKey: ["org", org.id] }); onChanged?.(); };
  const toggleArchive = useMutation({
    mutationFn: async () => (await api.post(`/admin/organisations/${org.id}/${org.archived ? "restore" : "archive"}`)).data,
    onSuccess: () => { toast.success(org.archived ? "Organisation restored" : "Organisation archived"); refresh(); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const stop = (e) => e.stopPropagation();
  return (
    <div onClick={stop}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`org-actions-${org.id}`}><MoreHorizontal className="h-4 w-4" /></Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onClick={stop}>
          <DropdownMenuItem onSelect={() => setDialog("edit")} data-testid="org-action-edit"><Pencil className="mr-2 h-4 w-4" /> Edit</DropdownMenuItem>
          <DropdownMenuItem onSelect={() => toggleArchive.mutate()} data-testid="org-action-archive">
            {org.archived ? <><ArchiveRestore className="mr-2 h-4 w-4" /> Restore</> : <><Archive className="mr-2 h-4 w-4" /> Archive</>}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => setDialog("delete")} className="text-red-600 focus:text-red-600" data-testid="org-action-delete">
            <Trash2 className="mr-2 h-4 w-4" /> Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <EditDialog org={org} open={dialog === "edit"} onOpenChange={(v) => setDialog(v ? "edit" : null)} onDone={refresh} />
      <DeleteDialog org={org} open={dialog === "delete"} onOpenChange={(v) => setDialog(v ? "delete" : null)}
        onDeleted={() => { qc.invalidateQueries({ queryKey: ["orgs"] }); onDeleted?.(); }} />
    </div>
  );
};
